export class HeadlineTexture {
  /**
   * Headlines with two or fewer rendered rows should show one texture line.
   */
  static maxRowsWithOneTextureLine = 2;

  /**
   * Headlines with three or four rendered rows should show two texture lines.
   * Longer headlines should show three texture lines.
   */
  static maxRowsWithTwoTextureLines = 4;

  /**
   * Component-specific selectors that use the same headline texture behavior.
   * Texture modifier class names are derived from wrapperClass.
   */
  static components = [
    {
      wrapperClass: 'campaign-slide__heading-wrapper',
      titleClass: 'campaign-slide__title',
      imageClass: 'campaign-slide__overlay-image'
    },
    {
      wrapperClass: 'campaign-media-heading-wrapper',
      titleClass: 'campaign-media-heading',
      imageClass: 'campaign-media-overlay-image'
    },
    {
      wrapperClass: 'promo-banner-main',
      titleClass: 'promo-banner-title-primary',
      imageClass: 'promo-banner-image'
    }
  ];

  /**
   * Converts a class name into a jQuery selector.
   * @param {String} className - CSS class name without the leading dot.
   * @returns {String} CSS class selector.
   */
  static getClassSelector(className) {
    return `.${className}`;
  }

  /**
   * Builds texture modifier class names from the component wrapper class.
   * @param {String} wrapperClass - Base wrapper class for the component.
   * @returns {Object} Texture modifier classes for one-line, two-line and three-line states.
   */
  static getTextureClasses(wrapperClass) {
    return {
      oneLineClass: `${wrapperClass}--texture-one-line`,
      twoLinesClass: `${wrapperClass}--texture-two-lines`,
      threeLinesClass: `${wrapperClass}--texture-three-lines`
    };
  }

  /**
   * Checks whether any supported headline texture component exists on the page.
   * @returns {Boolean}
   */
  static isOnPage() {
    return HeadlineTexture.components.some(component => (
      $(HeadlineTexture.getClassSelector(component.wrapperClass)).length > 0
    ));
  }

  /**
   * Initializes texture syncing and keeps a resize timeout reference for debouncing.
   */
  constructor() {
    this.resizeTimeout = null;
    this.init();
  }

  /**
   * Measures each configured headline and applies the matching texture modifier class.
   * Only wrappers that contain the expected texture image are updated.
   */
  syncTextureClasses() {
    HeadlineTexture.components.forEach(component => {
      const wrapperSelector = HeadlineTexture.getClassSelector(component.wrapperClass);
      const titleSelector = HeadlineTexture.getClassSelector(component.titleClass);
      const imageSelector = HeadlineTexture.getClassSelector(component.imageClass);
      const textureClasses = HeadlineTexture.getTextureClasses(component.wrapperClass);

      $(wrapperSelector).each((index, el) => {
        const $wrapper = $(el);
        const $title = $wrapper.find(titleSelector).first();

        if (!$title.length || !$wrapper.find(imageSelector).length) {
          return;
        }

        const title = $title.get(0);
        const titleStyles = window.getComputedStyle(title);
        const lineHeight = parseFloat(titleStyles.lineHeight) || parseFloat(titleStyles.fontSize) * 1.2;

        if (!lineHeight) {
          return;
        }

        const rowCount = Math.round(title.getBoundingClientRect().height / lineHeight);
        let textureClass = textureClasses.threeLinesClass;

        if (rowCount <= HeadlineTexture.maxRowsWithOneTextureLine) {
          textureClass = textureClasses.oneLineClass;
        } else if (rowCount <= HeadlineTexture.maxRowsWithTwoTextureLines) {
          textureClass = textureClasses.twoLinesClass;
        }

        $wrapper
          .removeClass(`${textureClasses.oneLineClass} ${textureClasses.twoLinesClass} ${textureClasses.threeLinesClass}`)
          .addClass(textureClass);
      });
    });
  }

  /**
   * Runs the initial sync, repeats it after fonts load, and resyncs after resize.
   */
  init() {
    this.syncTextureClasses();

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.syncTextureClasses());
    }

    $(window).on('resize', () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => this.syncTextureClasses(), 100);
    });
  }
}
